// Auth module — login, signup, PIN, invitations.

import { fb } from '../../core/firebase.js';
import { state } from '../../core/state.js';
import { userProfileRef, invitationRef, metaRef } from '../../core/paths.js';
import { isEmail, isPin, isNonEmpty } from '../../utils/validation.js';
import { toast } from '../../components/toast.js';

export async function signUp({ email, password, familyName }) {
  if (!isEmail(email)) throw new Error('Correo inválido');
  if (!password || password.length < 6) throw new Error('Contraseña mínimo 6 caracteres');
  if (!isNonEmpty(familyName)) throw new Error('Ingresa el nombre de la familia');

  const cred = await fb.signUp(fb.auth, email, password);
  const uid = cred.user.uid;

  state.familyId = uid;
  state.uid = uid;
  state.role = 'owner';

  await fb.setDoc(metaRef(), {
    familyName: familyName.trim(),
    lang: 'es',
    adminPin: '1234',
    setupComplete: false,
    createdAt: Date.now(),
  });

  await fb.setDoc(userProfileRef(uid), {
    uid,
    familyId: uid,
    role: 'owner',
    email,
    createdAt: Date.now(),
  });

  return cred.user;
}

export async function signIn({ email, password }) {
  if (!isEmail(email)) throw new Error('Correo inválido');
  const cred = await fb.signIn(fb.auth, email, password);
  return cred.user;
}

export async function logout() {
  await fb.signOut(fb.auth);
  state.familyId = null;
  state.uid = null;
  state.selectedHeroId = null;
  state.isHeroMode = false;
  state.userProfile = null;
}

export function onAuthChange(cb) {
  return fb.onAuth(fb.auth, async (user) => {
    if (!user) {
      state.uid = null;
      state.familyId = null;
      cb(null);
      return;
    }
    state.uid = user.uid;
    // Resolve familyId from user_profile
    try {
      const snap = await fb.getDoc(userProfileRef(user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        state.userProfile = profile;
        state.familyId = profile.familyId || user.uid;
        state.role = profile.role || 'owner';
      } else {
        state.familyId = user.uid; // owner default
        state.role = 'owner';
      }
    } catch (e) {
      console.warn('[auth] profile resolve failed', e);
      state.familyId = user.uid;
    }
    cb(user);
  });
}

export async function validateAdminPin(pin) {
  if (!isPin(pin)) return false;
  try {
    // Prefer cached state, fall back to live read
    let adminPin = state.meta?.adminPin;
    if (!adminPin) {
      const snap = await fb.getDoc(metaRef());
      if (!snap.exists()) return pin === '1234'; // Default for unset families
      const meta = snap.data();
      state.meta = meta;
      adminPin = meta.adminPin || '1234';
    }
    return adminPin === pin;
  } catch (e) {
    console.error('[validateAdminPin]', e);
    return false;
  }
}

export async function validateHeroPin(heroId, pin) {
  if (!isPin(pin)) return false;
  try {
    // Prefer cached state, fall back to live read
    let hero = state.heroes.find(h => h.id === heroId);
    if (!hero) {
      const snap = await fb.getDoc(fb.doc(fb.db, 'families', state.familyId, 'heroes', heroId));
      if (!snap.exists()) return false;
      hero = { id: heroId, ...snap.data() };
    }
    return hero.pin === pin;
  } catch (e) {
    console.error('[validateHeroPin]', e);
    return false;
  }
}

export async function changeAdminPin(newPin) {
  if (!isPin(newPin)) throw new Error('PIN debe tener 4 dígitos');
  await fb.updateDoc(metaRef(), { adminPin: newPin });
  toast('PIN actualizado', { type: 'success' });
}

// Invitations
export async function generateInvitation() {
  const code = Math.random().toString(36).slice(2, 8).toUpperCase();
  const expiresAt = Date.now() + 48 * 60 * 60 * 1000; // 48h
  await fb.setDoc(invitationRef(code), {
    code,
    familyId: state.familyId,
    createdBy: state.uid,
    createdAt: Date.now(),
    expiresAt,
    used: false,
  });
  return { code, expiresAt };
}

export async function acceptInvitation(code) {
  const snap = await fb.getDoc(invitationRef(code));
  if (!snap.exists()) throw new Error('Código inválido');
  const inv = snap.data();
  if (inv.used) throw new Error('Código ya utilizado');
  if (inv.expiresAt < Date.now()) throw new Error('Código expirado');

  await fb.updateDoc(invitationRef(code), { used: true, usedAt: Date.now(), usedBy: state.uid });
  await fb.setDoc(userProfileRef(state.uid), {
    uid: state.uid,
    familyId: inv.familyId,
    role: 'maestro',
    // Requerido por firestore.rules: prueba de que la pertenencia a esta
    // familia viene de una invitación real y no de un familyId inventado.
    inviteCode: code,
    joinedAt: Date.now(),
  });

  state.familyId = inv.familyId;
  state.role = 'maestro';
  return inv.familyId;
}

// Bridge to legacy code during migration
if (typeof window !== 'undefined') {
  window.authModule = { signUp, signIn, logout, validateAdminPin, validateHeroPin, changeAdminPin, generateInvitation, acceptInvitation };
}
